import {
  getV1CompaniesCompanyIdSites,
  getV1CompaniesCompanyIdSitesId,
  patchV1CompaniesCompanyIdSitesId,
  postV1CompaniesCompanyIdSites,
  SiteType,
  TimeZone,
} from '@treadinc/horizon-api-spec';
import { convertLength } from '@turf/helpers';
import { t } from 'i18next';

import { Site } from '~hooks/useSites/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { resolveSiteTimeZone } from '~utils/siteTimeZone';
import { lookupTimeZone } from '~utils/timeZoneLookup';

import { EntityCSVConfig } from './types';

// Derive valid site types from the SiteType enum (EQUIPMENT_HOME_BASE excluded as it's not user-selectable)
const VALID_SITE_TYPES = Object.values(SiteType).filter(
  (v) => v !== SiteType.EQUIPMENT_HOME_BASE,
);
const VALID_GEOFENCE_TYPES = ['NONE', 'CIRCLE', 'POLYGON'];

function normalizeSiteType(input: string | undefined): SiteType | undefined {
  if (!input) return undefined;
  const cleaned = input.trim().replace(/\s/g, '').toUpperCase();
  return VALID_SITE_TYPES.find((v) => v.toUpperCase() === cleaned) as
    | SiteType
    | undefined;
}

// Spreadsheet apps drop the leading zero and may add seconds, so "5:00" and "05:00:00"
// are accepted and sent as "05:00".
const WALL_CLOCK_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/;

function normalizeWallClock(input: string | undefined): string | undefined {
  const match = input?.trim().match(WALL_CLOCK_PATTERN);

  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : undefined;
}

// Hours need a zone to anchor them, so a row that would leave hours without one fails.
function throwUnlessHoursHaveZone(timeZone: TimeZone | null, siteHasHours: boolean) {
  if (!timeZone && siteHasHours) {
    throw new Error(t('csv_import_export.errors.no_time_zone_for_coords'));
  }
}

function lookupTimeZoneIfLocated(
  lat: string | null | undefined,
  lon: string | null | undefined,
) {
  return lat && lon ? lookupTimeZone(Number(lat), Number(lon)) : Promise.resolve(null);
}

async function inferCreatedTimeZone(
  lat: string | null,
  lon: string | null,
  siteHasHours: boolean,
  companyTimeZone: TimeZone | null,
) {
  const timeZone = resolveSiteTimeZone({
    areHoursBeingSetOnZonelessSite: siteHasHours,
    companyTimeZone,
    currentTimeZone: null,
    inferredTimeZone: await lookupTimeZoneIfLocated(lat, lon),
    isLocationNew: true,
  });
  throwUnlessHoursHaveZone(timeZone, siteHasHours);

  return timeZone ?? undefined;
}

async function inferUpdatedTimeZone(
  companyId: string,
  id: string,
  lat: string | undefined,
  lon: string | undefined,
  openAt: string | null | undefined,
  closeAt: string | null | undefined,
  companyTimeZone: TimeZone | null,
) {
  const rowHasCoordinates = Boolean(lat && lon);
  const rowSetsHours = Boolean(openAt || closeAt);
  if (!rowHasCoordinates && !rowSetsHours) return undefined;

  let site;
  try {
    site = (
      await getV1CompaniesCompanyIdSitesId({ path: { 'company-id': companyId, id } })
    ).data.data;
  } catch {
    throw new Error(t('csv_import_export.errors.site_not_loaded', { id }));
  }

  const isLocationNew =
    rowHasCoordinates &&
    (!site.lat ||
      !site.lon ||
      Number(site.lat) !== Number(lat) ||
      Number(site.lon) !== Number(lon));
  const areHoursBeingSetOnZonelessSite = !site.time_zone && rowSetsHours;
  if (!isLocationNew && !areHoursBeingSetOnZonelessSite) return undefined;

  const timeZone = resolveSiteTimeZone({
    areHoursBeingSetOnZonelessSite,
    companyTimeZone,
    currentTimeZone: site.time_zone ?? null,
    inferredTimeZone: await lookupTimeZoneIfLocated(lat ?? site.lat, lon ?? site.lon),
    isLocationNew,
  });
  // A file without an hours column keeps the saved hours.
  const siteHasHours = Boolean(
    (openAt === undefined ? site.open_at : openAt) ||
    (closeAt === undefined ? site.close_at : closeAt),
  );
  throwUnlessHoursHaveZone(timeZone, siteHasHours);

  return timeZone === site.time_zone ? undefined : timeZone;
}

// A blank cell clears hours on update; a file without the column leaves them alone.
function wallClockUpdate(data: Record<string, string>, key: 'open_at' | 'close_at') {
  return key in data ? (normalizeWallClock(data[key]) ?? null) : undefined;
}

const sitesColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing ID (for updates only)',
  },
  {
    key: 'name',
    header: 'name',
    required: true,
    exampleValue: 'Main Quarry',
    description: 'Site name',
  },
  {
    key: 'site_type',
    header: 'site_type',
    required: true,
    enumValues: VALID_SITE_TYPES,
    exampleValue: SiteType.QUARRY,
    description: 'Site type (case-insensitive)',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'SITE-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'department',
    header: 'department',
    required: false,
    exampleValue: '',
    description: 'Department (must exist in system)',
  },
  {
    key: 'address_line_1',
    header: 'address_line_1',
    required: false,
    exampleValue: '123 Quarry Rd',
    description: 'Street address (required if no lat/lon)',
  },
  {
    key: 'address_city',
    header: 'address_city',
    required: false,
    exampleValue: 'Austin',
    description: 'City (required if no lat/lon)',
  },
  {
    key: 'address_state',
    header: 'address_state',
    required: false,
    exampleValue: 'TX',
    description: '2-letter state code (required if no lat/lon)',
  },
  {
    key: 'address_zip',
    header: 'address_zip',
    required: false,
    exampleValue: '78701',
    description: 'ZIP code (required if no lat/lon)',
  },
  {
    key: 'latitude',
    header: 'latitude',
    required: false,
    exampleValue: '30.2672',
    description: 'Latitude (required if no address)',
  },
  {
    key: 'longitude',
    header: 'longitude',
    required: false,
    exampleValue: '-97.7431',
    description: 'Longitude (required if no address)',
  },
  {
    key: 'geofence_type',
    header: 'geofence_type',
    required: true,
    enumValues: VALID_GEOFENCE_TYPES,
    exampleValue: 'CIRCLE',
    description: 'NONE or CIRCLE (POLYGON requires UI)',
  },
  {
    key: 'radius',
    header: 'radius',
    required: false,
    exampleValue: '500',
    description: 'Radius in feet (required for CIRCLE, 1-50000)',
  },
  {
    key: 'contact_name',
    header: 'contact_name',
    required: false,
    exampleValue: '',
    description: 'Site contact name',
  },
  {
    key: 'contact_phone',
    header: 'contact_phone',
    required: false,
    exampleValue: '',
    description: 'Phone with country code, digits only (e.g., +14155551234)',
  },
  {
    key: 'notes',
    header: 'notes',
    required: false,
    exampleValue: '',
    description: 'Additional notes',
  },
  {
    key: 'open_at',
    header: 'open_at',
    required: false,
    exampleValue: '05:00',
    description:
      "Opening time, 24-hour HH:MM in the site's local time (requires close_at)",
  },
  {
    key: 'close_at',
    header: 'close_at',
    required: false,
    exampleValue: '16:30',
    description:
      "Closing time, 24-hour HH:MM in the site's local time (requires open_at)",
  },
];

export const createSitesConfig = (
  getDepartmentIdByName: (name: string) => string | undefined,
  companyTimeZone: TimeZone | null,
  configCompanyId: string,
): EntityCSVConfig<Site> => ({
  entityType: 'sites',
  columns: sitesColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdSites({ path: { 'company-id': companyId }, query }),
      Site.parse,
    ),

  create: async (companyId: string, data: Record<string, string>): Promise<Site> => {
    const departmentId = data.department
      ? getDepartmentIdByName(data.department)
      : undefined;

    const lat = data.latitude?.trim() || null;
    const lon = data.longitude?.trim() || null;
    const openAt = normalizeWallClock(data.open_at);
    const closeAt = normalizeWallClock(data.close_at);

    const geofenceTypeStr = data.geofence_type?.toUpperCase();
    // NONE means no geofence - only CIRCLE and POLYGON are valid API values
    const hasGeofence = geofenceTypeStr && geofenceTypeStr !== 'NONE';

    // Build next_billion_geofence for CIRCLE type
    let nextBillionGeofence: Record<string, unknown> | undefined;
    if (hasGeofence && geofenceTypeStr === 'CIRCLE' && lat && lon && data.radius) {
      // Radius in CSV is in feet, convert to meters for API
      const radiusMeters = convertLength(Number(data.radius), 'feet', 'meters');
      nextBillionGeofence = {
        geofence: {
          name: data.name,
          type: 'circle',
          circle_center: { lat: Number(lat), lon: Number(lon) },
          circle_radius: radiusMeters,
        },
        moving_geofence: false,
      };
    }

    const response = await postV1CompaniesCompanyIdSites({
      path: { 'company-id': companyId },
      body: {
        name: data.name,
        company_id: companyId,
        site_type: normalizeSiteType(data.site_type ?? '') as SiteType,
        external_id: data.external_id || undefined,
        department_id: departmentId || null,
        lat,
        lon,
        notes: data.notes || undefined,
        address: data.address_line_1
          ? {
              thoroughfare: data.address_line_1,
              locality: data.address_city || '',
              administrative_area: data.address_state || '',
              postal_code: data.address_zip || '',
              country: 'US',
            }
          : undefined,
        next_billion_geofence: nextBillionGeofence,
        open_at: openAt,
        close_at: closeAt,
        time_zone: await inferCreatedTimeZone(
          lat,
          lon,
          Boolean(openAt || closeAt),
          companyTimeZone,
        ),
      },
    });

    return Site.parse(response.data.data);
  },

  update: async (id: string, data: Record<string, string>): Promise<Site> => {
    const departmentId = data.department
      ? getDepartmentIdByName(data.department)
      : undefined;

    const lat = data.latitude?.trim() || undefined;
    const lon = data.longitude?.trim() || undefined;
    const openAt = wallClockUpdate(data, 'open_at');
    const closeAt = wallClockUpdate(data, 'close_at');

    const geofenceTypeStr = data.geofence_type?.toUpperCase();
    // NONE means no geofence - only CIRCLE and POLYGON are valid API values
    const hasGeofence = geofenceTypeStr && geofenceTypeStr !== 'NONE';

    // Build next_billion_geofence for CIRCLE type
    let nextBillionGeofence: Record<string, unknown> | undefined;
    if (hasGeofence && geofenceTypeStr === 'CIRCLE' && lat && lon && data.radius) {
      // Radius in CSV is in feet, convert to meters for API
      const radiusMeters = convertLength(Number(data.radius), 'feet', 'meters');
      nextBillionGeofence = {
        geofence: {
          name: data.name,
          type: 'circle',
          circle_center: { lat: Number(lat), lon: Number(lon) },
          circle_radius: radiusMeters,
        },
        moving_geofence: false,
      };
    }

    const response = await patchV1CompaniesCompanyIdSitesId({
      path: { 'company-id': configCompanyId, id },
      body: {
        name: data.name,
        site_type: normalizeSiteType(data.site_type ?? '') as SiteType,
        external_id: data.external_id || undefined,
        department_id: departmentId || null,
        lat,
        lon,
        notes: data.notes || undefined,
        address: data.address_line_1
          ? {
              thoroughfare: data.address_line_1,
              locality: data.address_city || '',
              administrative_area: data.address_state || '',
              postal_code: data.address_zip || '',
              country: 'US',
            }
          : undefined,
        next_billion_geofence: nextBillionGeofence,
        open_at: openAt,
        close_at: closeAt,
        time_zone: await inferUpdatedTimeZone(
          configCompanyId,
          id,
          lat,
          lon,
          openAt,
          closeAt,
          companyTimeZone,
        ),
      },
    });

    return Site.parse(response.data.data);
  },

  transformExport: (site: Site): Record<string, string> => {
    const geofence = site.nextBillionGeofence;
    let geofenceType = 'NONE';
    let radius = '';

    if (geofence?.geofenceType) {
      geofenceType = geofence.geofenceType.toUpperCase();
    } else if (geofence?.type) {
      geofenceType = geofence.type.toUpperCase();
    }

    if (geofence?.circleRadius) {
      // Convert radius from meters to feet for display
      radius = Math.round(
        convertLength(geofence.circleRadius, 'meters', 'feet'),
      ).toString();
    }

    return {
      id: site.id,
      name: site.name,
      site_type: site.siteType?.replace(/\s/g, '') || '',
      external_id: site.externalId || '',
      department: site.department?.name || '',
      address_line_1: site.address?.streetAddress || '',
      address_city: site.address?.locality || '',
      address_state: site.address?.administrativeArea || '',
      address_zip: site.address?.postalCode || '',
      latitude: site.lat?.toString() || '',
      longitude: site.lon?.toString() || '',
      geofence_type: geofenceType,
      radius,
      contact_name: site.contacts?.[0]?.name || '',
      contact_phone: site.contacts?.[0]?.phone || '',
      notes: site.notes || '',
      open_at: site.openAt || '',
      close_at: site.closeAt || '',
    };
  },

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    name: row.name?.trim(),
    site_type: normalizeSiteType(row.site_type ?? '') ?? row.site_type?.trim(),
    external_id: row.external_id?.trim() || null,
    department: row.department?.trim() || null,
    latitude: row.latitude?.trim() || null,
    longitude: row.longitude?.trim() || null,
    geofence_type: row.geofence_type?.toUpperCase() || 'NONE',
    radius: row.radius?.trim() || null,
    notes: row.notes?.trim() || null,
    open_at: normalizeWallClock(row.open_at) ?? null,
    close_at: normalizeWallClock(row.close_at) ?? null,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    if (!row.name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'name' }));
    }

    if (!row.site_type?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'site_type' }));
    } else if (!normalizeSiteType(row.site_type)) {
      errors.push(
        t('csv_import_export.errors.invalid_enum', {
          field: 'site_type',
          values: VALID_SITE_TYPES.join(', '),
        }),
      );
    }

    const hasAddress =
      row.address_line_1?.trim() &&
      row.address_city?.trim() &&
      row.address_state?.trim() &&
      row.address_zip?.trim();
    const hasCoordinates = row.latitude?.trim() && row.longitude?.trim();

    if (!hasAddress && !hasCoordinates) {
      errors.push(t('csv_import_export.errors.address_or_coords_required'));
    }

    // Validate latitude and longitude are valid numbers if provided
    if (row.latitude?.trim()) {
      const lat = Number(row.latitude);
      if (isNaN(lat)) {
        errors.push(t('csv_import_export.errors.invalid_number', { field: 'latitude' }));
      } else if (lat < -90 || lat > 90) {
        errors.push(t('csv_import_export.errors.latitude_range', { min: -90, max: 90 }));
      }
    }

    if (row.longitude?.trim()) {
      const lon = Number(row.longitude);
      if (isNaN(lon)) {
        errors.push(t('csv_import_export.errors.invalid_number', { field: 'longitude' }));
      } else if (lon < -180 || lon > 180) {
        errors.push(
          t('csv_import_export.errors.longitude_range', { min: -180, max: 180 }),
        );
      }
    }

    if (!row.geofence_type?.trim()) {
      errors.push(
        t('csv_import_export.errors.required_field', { field: 'geofence_type' }),
      );
    } else if (!VALID_GEOFENCE_TYPES.includes(row.geofence_type.toUpperCase())) {
      errors.push(
        t('csv_import_export.errors.invalid_enum', {
          field: 'geofence_type',
          values: VALID_GEOFENCE_TYPES.join(', '),
        }),
      );
    }

    if (row.geofence_type?.toUpperCase() === 'CIRCLE') {
      // CIRCLE geofence requires coordinates (lat/lon) to create the geofence
      if (!hasCoordinates) {
        errors.push(t('csv_import_export.errors.coords_required_for_circle'));
      }
      if (!row.radius?.trim()) {
        errors.push(t('csv_import_export.errors.radius_required_for_circle'));
      } else {
        const radius = Number(row.radius);
        if (isNaN(radius)) {
          errors.push(t('csv_import_export.errors.invalid_number', { field: 'radius' }));
        } else if (radius < 1) {
          errors.push(
            t('csv_import_export.errors.number_min', { field: 'radius', min: 1 }),
          );
        } else if (radius > 50000) {
          errors.push(
            t('csv_import_export.errors.number_max', { field: 'radius', max: 50000 }),
          );
        }
      }
    }

    const openAt = row.open_at?.trim();
    const closeAt = row.close_at?.trim();

    if (Boolean(openAt) !== Boolean(closeAt)) {
      errors.push(t('csv_import_export.errors.hours_require_open_and_close'));
    }

    if (openAt && !normalizeWallClock(openAt)) {
      errors.push(
        t('csv_import_export.errors.invalid_wall_clock_time', { field: 'open_at' }),
      );
    }

    if (closeAt && !normalizeWallClock(closeAt)) {
      errors.push(
        t('csv_import_export.errors.invalid_wall_clock_time', { field: 'close_at' }),
      );
    }

    return errors;
  },
});
